/**
 * N8ONE — Editorial Image Registry
 *
 * ONE SOURCE OF TRUTH for every prominent editorial photograph used
 * across the app. If you're adding, replacing, or auditing imagery,
 * start here.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE RULE
 * ─────────────────────────────────────────────────────────────────
 *   ONE PROMINENT PHOTOGRAPH = ONE PRIMARY LOCATION.
 *
 * A hero / editorial photograph must not appear on multiple
 * unrelated pages. Before assigning a new prominent editorial photo,
 * grep this file — if it's already assigned somewhere, choose another
 * image (unless the repetition is intentionally approved and
 * documented alongside its second use).
 *
 * The exception is brand assets (logo, mark, subtle repeatable
 * decorative textures). Lifestyle / editorial photography is NEVER
 * exempt.
 *
 * ─────────────────────────────────────────────────────────────────
 * WHAT LIVES HERE (semantic keys)
 * ─────────────────────────────────────────────────────────────────
 *   meditationArrive       → Rituals → Mind → Meditation, Beginner chapter
 *   meditationDeepen       → Rituals → Mind → Meditation, Intermediate chapter
 *   meditationExpand       → Rituals → Mind → Meditation, Advanced chapter
 *
 *   soulInnerChild         → Soul → Inner Exploration → Inner Child
 *   soulShadow             → Soul → Inner Exploration → The Shadow
 *   soulSelfInquiry        → Soul → Inner Exploration → Self-Inquiry
 *   soulForgiveness        → Soul → Inner Exploration → Forgiveness
 *   soulGrief              → Soul → Inner Exploration → Grief & Letting Go
 *   soulBoundaries         → Soul → Inner Exploration → Boundaries
 *
 *   soulPathRegulation     → Soul landing → Regulation & Presence path card
 *   soulPathExploration    → Soul landing → Inner Exploration path card
 *
 *   regulationBreath       → Soul → Regulation → Breath sub-card
 *   regulationNervous      → Soul → Regulation → Nervous System sub-card
 *
 *   breathConscious        → Soul → Breath → Conscious Breathing
 *   breathExtendedExhale   → Soul → Breath → Extended Exhale
 *   breathBox              → Soul → Breath → Box Breathing
 *   breathPhysiological    → Soul → Breath → Physiological Sigh
 *   breathCoherent         → Soul → Breath → Coherent Breathing
 *
 *   communityCover1..5     → Community page hero (daily rotator, this page only)
 *
 * ─────────────────────────────────────────────────────────────────
 * WHAT DOES NOT LIVE HERE
 * ─────────────────────────────────────────────────────────────────
 * Surface heroes (Home, Becoming, Rituals, Mirror, Journal, Profile,
 * Tracking, Soul landing, etc.) still come from `heroImages.js`, which
 * is a per-surface, per-season, per-day rotator — deliberately a shared
 * pool because each surface only shows one image at a time and the
 * whole point is seasonal variation. The rule above applies to fixed
 * editorial moments, not to that rotator.
 *
 * Quiet Reads hero images are colocated with each article in
 * `lib/quietReads.js` (one image per article, unique to that article).
 */

const U = (id, w = 1800) =>
  `https://images.unsplash.com/${id}?w=${w}&q=80&auto=format&fit=crop`;

export const EDITORIAL = {
  // ── Meditation chapter journey (ARRIVE → DEEPEN → EXPAND) ──────────
  // Sole owners. These three exact URLs appear NOWHERE else in the app.
  meditationArrive:     U("photo-1439066615861-d1af74d74000"), // still water at first light
  meditationDeepen:     U("photo-1447752875215-b2761acb3c5d"), // filtered light through misted forest
  meditationExpand:     U("photo-1470071459604-3b5ec3a7fe05"), // atmospheric mountain ridges receding

  // ── Soul → Inner Exploration (6 practices, each unique) ────────────
  soulInnerChild:       U("photo-1500964757637-c85e8a162699"), // warm meadow at dusk — tenderness
  soulShadow:           U("photo-1518241353330-0f7941c2d9b5"), // a figure held in soft mist — hidden parts
  soulSelfInquiry:      U("photo-1490730141103-6cac27aaab94"), // open notebook by a window — honest asking
  soulForgiveness:      U("photo-1602934585418-f588bea4215c"), // warm candles at low light — release
  soulGrief:            U("photo-1502082553048-f009c37129b9"), // quiet mist over a lake — allowing
  soulBoundaries:       U("photo-1470252649378-9c29740c9fa8"), // emerald moss on forest floor — grounded

  // ── Soul → landing path cards ──────────────────────────────────────
  soulPathRegulation:   U("photo-1544161515-4ab6ce6db874"),    // spa stones with a leaf — steadiness
  soulPathExploration:  U("photo-1441829266145-6d4bfb7a1029"), // soft light through a quiet forest — inward

  // ── Soul → Regulation & Presence sub-cards ─────────────────────────
  regulationBreath:     U("photo-1519823551278-64ac92734fb1"), // spa candles glowing — breath, warmth
  regulationNervous:    U("photo-1544367567-0f2fcb009e0b"),    // hands with fresh herbs — bodily attention

  // ── Soul → Breath (5 practices, each unique) ───────────────────────
  breathConscious:      U("photo-1512290923902-8a9f81dc236c"), // serene face in half-light — presence
  breathExtendedExhale: U("photo-1518709268805-4e9042af2176"), // morning ridge held in gentle mist — releasing
  breathBox:            U("photo-1508739773434-c26b3d09e071"), // stacked river stones — steady structure
  breathPhysiological:  U("photo-1506126613408-eca07ce68773"), // a single silhouette at open sky — one clear breath
  breathCoherent:       U("photo-1520962880247-cfaf541c8724"), // horizon at atmospheric light — steady rhythm

  // ── Community page daily cover rotator ─────────────────────────────
  // Broader visual territory: hands, materials, warm interiors, human
  // detail, everyday textures — NOT more nature landscapes. This mix
  // keeps the Community surface visually distinct from the nature-heavy
  // Meditation / Soul / Breath surfaces above.
  communityCover1:      U("photo-1519681393784-d120267933ba"), // hands connecting on a table — quiet detail
  communityCover2:      U("photo-1455390582262-044cdead277a"), // a quiet workspace with soft natural light — gathered simply
  communityCover3:      U("photo-1509909756405-be0199881695"), // linen fabric catching morning light — softness
  communityCover4:      U("photo-1465146344425-f00d5f5c8f07"), // wildflowers in a shaft of light — offered warmth
  communityCover5:      U("photo-1490645935967-10de6ba17061"), // a set table lit by candles — gathered simply

  // ── Rituals → Mind → Daily Affirmations transition image ───────────
  // The one editorial moment on the Affirmations page — a quiet human
  // moment in beautiful natural morning light that functions as the
  // bridge between READING affirmations and LIVING them. Deliberately
  // human (not more nature) and distinct from every other prominent
  // editorial moment in the app. No embedded text.
  affirmationsTransition: U("photo-1499750310107-5fef28a66643"), // an open journal + warm coffee in soft morning light — hands at the start of a reflective moment

  // ── Rituals → Mind → Daily Intentions action image ─────────────────
  // The one editorial moment on the Daily Intentions page — a quiet,
  // candid human moment that communicates INTENTION BECOMING ACTION:
  // hands preparing tea in soft morning light. Related in rhythm to
  // `affirmationsTransition` (wide, shallow, warm, human) but a clearly
  // distinct composition. Verified unique within N8ONE.
  intentionsAction: U("photo-1544787219-7f47ccb76574"), // hands pouring tea in soft morning light — a small intentional everyday act
};

export default EDITORIAL;
